import {Perlin} from "@utility/perlin";
import {Vector2} from "@utility/vector2";
import {ITile, Tile} from "./tile";
import {Random} from "@utility/random";
import {MapSize} from "@game/map/mapSize";
import {Region} from "@game/map/region";
import {Game} from "@game/game";
import GeographyData from "@data/geography.json"
import {Place} from "@game/map/place";

const PERLIN_SIZE = 200;

export function computeAltitude(x: number, y: number, seed: number) {
    const phase = (seed % 1000) / 1000 * Math.PI * 2;
    const wave1 = Math.sin(x * 0.07 + y * 0.05 + phase);
    const wave2 = Math.sin(x * 0.04 - y * 0.08 + phase * 1.3);
    return 0.5 + 0.4 * (wave1 * 0.6 + wave2 * 0.4);
}

type TBiome = "ice" | "tundra" | "desert" | "forest" | "grasslands" | "hills" | "jungle" | "savannah";

const GEOGRAPHY = GeographyData as IGeography;

interface IBiome {
    places: Record<string, string[]>
    ground: string[]
    likelihood: number
}
export interface IGeography {
    biomes: Record<TBiome, IBiome>
}

export class Geography {
    private perlin: Perlin;
    private _geography: IGeography;

    constructor(private size: MapSize, private seed: number) {
        this._geography = GEOGRAPHY;
        this.perlin = new Perlin(PERLIN_SIZE, new Random(seed));
    }

    getPlaceImage(tile: Tile, place: Place) {
        const position = tile.region.position;
        const biome = this.getBiome(tile.region.position);
        const places = this._geography.biomes[biome].places[place.type] ?? [];
        const select = (position.x * this.size.mapTiles.x + position.y) % places.length;

        return places[select];
    }

    getRegionTiles(region: Region) {
        const tiles: ITile[] = [];
        const offset = region.position.multiply(this.size.regionTiles);
        const biome = this.getBiome(region.position);

        for (let x = 0; x < this.size.regionTiles.x; ++x) {
            for (let y = 0; y < this.size.regionTiles.y; ++y) {
                const regionOffset = new Vector2(x, y);
                const tileId = this.size.getTileId(regionOffset);
                const position = offset.plus(regionOffset);
                const groundImage = this.getGroundImage(biome, position);

                const altitude = this.getAltitude(position);
                tiles.push({tileId, position, region, regionOffset, biome, groundImage, altitude});
            }
        }
        return tiles;
    }
    getRegionOffset(region: Vector2, position: Vector2) {
        const offset = position.minus(region.multiply(this.size.regionTiles));
        const halfRegion = this.size.regionTiles.multiply(0.5).floor();

        return new Vector2(offset.x < halfRegion.x ? -1 : 1, offset.y < halfRegion.y ? -1 : 1);
    }
    getRegionIdsAround(game: Game, tile: Vector2): number[] {
        const region = this.size.getTileRegion(tile);
        const regionOffset = this.getRegionOffset(region, tile);
        const offsets = [
            new Vector2(0, 0),
            new Vector2(regionOffset.x, 0),
            new Vector2(0, regionOffset.y),
            new Vector2(regionOffset.x, regionOffset.y),
        ]
        return offsets.map(offset => region.plus(offset))
            .filter(region => region.greaterOrEqual(Vector2.zero) && region.less(this.size.mapRegions))
            .map(position => this.size.getRegionId(position));
    }

    private getBiome(region: Vector2): TBiome {
        let perlin = this.getPerlin(region.multiply(this.size.regionTiles));
        const pair = Object.entries(this._geography.biomes)
            .find(([, biome]) => (perlin -= biome.likelihood) <= 0);

        if (! pair) {
            throw new Error(`Failed to find biome for region ${region}`);
        }
        return pair[0] as TBiome;
    }

    private getAltitude(tile: Vector2) {
        return computeAltitude(tile.x, tile.y, this.seed);
    }

    private getPerlin(tile: Vector2) {
        const normalised = tile.divide(this.size.mapTiles);

        if (normalised.greater(Vector2.one) || normalised.less(Vector2.zero)) {
            debugger;
        }
        return this.perlin.get(normalised);
    }

    private getGroundImage(biome: TBiome, position: Vector2) {
        const tiles = this._geography.biomes[biome].ground;
        const select = (position.x * this.size.mapTiles.x + position.y) % tiles.length;

        return tiles[select];
    }
}